import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ImagePlus,
  MessageSquare,
  Mic,
  Plus,
  Send,
  Sparkles,
  Square,
  X,
} from "lucide-react";

import {
  useAddConversationAttachment,
  useConversation,
  useConversationMessages,
  useConversations,
  useCreateConversation,
  useSendConversationMessage,
} from "@/api/queries";
import type { AnswerEvidence, ConversationMessage, ConversationSnapshot } from "@/api/types";
import type { RecommendedItem } from "./display";
import { Button } from "@/components/Button";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Photo } from "@/components/Photo";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { ProposalCard } from "./ProposalCard";
import { StatusBadge } from "@/components/StatusBadge";
import { Page } from "@/layout/Page";
import { PageHeader } from "@/layout/PageHeader";
import { formatRelative } from "@/lib/format";
import { useAssistantStream } from "./useAssistantStream";
import { useVoiceNote, type VoiceNote } from "./useVoiceNote";
import {
  CONFIDENCE_LABELS,
  documentCitation,
  documentReadingNote,
  evidenceLine,
  shortExcerpt,
  messagesFromPages,
  missingLabels,
  parameterLabel,
  parameterValue,
  parseRecommendation,
  recommendationTone,
  timingLine,
} from "./display";

/**
 * Une conversation avec HD-Vault (TICKET V1.5-01).
 *
 * **L'assistant ne répond pas encore, et l'écran le dit.** Ce ticket pose la
 * mémoire : le fil, les tours, ce que la demande a de précisé et ce qui lui
 * manque encore. L'orchestrateur qui répondra arrive plus tard, et afficher
 * une fausse réponse en attendant donnerait une idée fausse de ce que le
 * produit sait faire.
 *
 * Ce qui est réellement démontrable ici : un fil survit à un rafraîchissement,
 * l'historique se pagine, et trois messages successifs — « je cherche une
 * mèche », « pour du béton », « 1/2 pouce » — se replient en une seule demande.
 */

/** Au plus quatre photos par question : au-delà, ce n'est plus une question. */
const MAX_PHOTOS = 4;

interface PendingPhoto {
  id: string;
  name: string;
  /** Une URL locale : l'image n'est pas retéléchargée pour être montrée. */
  preview: string;
}

export function ConversationPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const create = useCreateConversation();

  const { data: list, isLoading: listing } = useConversations({ status: "ACTIVE", limit: 20 });

  // Sans fil dans l'URL, on reprend le plus récent : c'est ce qu'un opérateur
  // qui rouvre l'application attend, et ça rend la reprise après
  // rafraîchissement observable sans manipulation.
  useEffect(() => {
    if (conversationId || listing) return;
    const latest = list?.items[0];
    if (latest) navigate(`/assistant/${latest.id}`, { replace: true });
  }, [conversationId, listing, list, navigate]);

  async function startConversation() {
    const conversation = await create.mutateAsync({});
    navigate(`/assistant/${conversation.id}`);
  }

  return (
    <Page>
      <PageHeader
        title="Conversation"
        subtitle="Posez une question sur l'inventaire. HD-Vault répond à partir de ce qui est enregistré."
        actions={
          <Button size="sm" variant="secondary" onClick={() => void startConversation()}>
            <Plus className="size-4" aria-hidden />
            Nouveau fil
          </Button>
        }
      />

      {conversationId ? (
        <Thread conversationId={conversationId} />
      ) : listing ? (
        <LoadingState label="Chargement des conversations…" />
      ) : (
        <Card>
          <CardBody>
            <EmptyState
              icon={<MessageSquare className="size-5" aria-hidden />}
              title="Aucune conversation"
              description="Ouvrez un fil pour poser une question. HD-Vault en gardera le contexte d'un tour à l'autre."
              action={
                <Button onClick={() => void startConversation()} loading={create.isPending}>
                  Ouvrir un fil
                </Button>
              }
            />
          </CardBody>
        </Card>
      )}
    </Page>
  );
}

function Thread({ conversationId }: { conversationId: string }) {
  const detail = useConversation(conversationId);
  const history = useConversationMessages(conversationId);
  const send = useSendConversationMessage(conversationId);
  const attach = useAddConversationAttachment(conversationId);
  const live = useAssistantStream(conversationId);
  const [draft, setDraft] = useState("");
  const [queued, setQueued] = useState<string | null>(null);
  // Les photos déposées mais pas encore envoyées. L'aperçu est une URL locale :
  // l'image n'est pas retéléchargée pour être montrée à celui qui vient de la
  // prendre.
  const [photos, setPhotos] = useState<PendingPhoto[]>([]);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const busy = live.state !== "idle";

  // Les pages arrivent de la plus récente à la plus ancienne ; la lecture va
  // dans l'autre sens.
  const messages: ConversationMessage[] = useMemo(
    () => messagesFromPages(history.data?.pages),
    [history.data],
  );

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [messages.length, live.step]);

  // La voix pose le transcript dans le champ de saisie, et s'arrête là. C'est
  // le mode B du ticket : ce qui part est ce que la personne a relu, pas ce que
  // le moteur a cru entendre (V1.5-10 §14).
  const voice = useVoiceNote(conversationId, setDraft);

  if (detail.isLoading) return <LoadingState label="Chargement du fil…" />;
  if (detail.error) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }
  if (!detail.data) return null;

  async function pick(files: FileList | null) {
    if (!files?.length) return;
    setPhotoError(null);
    if (!navigator.onLine) {
      // Pas de file d'attente pour une photo (V1.5-05) : afficher « envoyé »
      // pour un fichier qui n'est jamais parti serait un mensonge, et sur un
      // chantier c'est le genre de mensonge qui coûte un aller-retour.
      setPhotoError("Hors ligne : les photos ne peuvent pas être envoyées. Le texte, oui.");
      return;
    }
    for (const file of Array.from(files).slice(0, MAX_PHOTOS - photos.length)) {
      try {
        const created = await attach.mutateAsync(file);
        setPhotos((current) => [
          ...current,
          { id: created.id, name: file.name, preview: URL.createObjectURL(file) },
        ]);
      } catch (caught) {
        setPhotoError(
          (caught as { message?: string })?.message ?? "Cette photo n'a pas pu être envoyée.",
        );
      }
    }
    if (picker.current) picker.current.value = "";
  }

  function drop(id: string) {
    setPhotos((current) => {
      const removed = current.find((photo) => photo.id === id);
      if (removed) URL.revokeObjectURL(removed.preview);
      return current.filter((photo) => photo.id !== id);
    });
  }

  async function submit() {
    const text = draft.trim();
    // Une photo seule est une question : « c'est quoi, ça ? ». Le texte n'est
    // obligatoire que s'il n'y a rien d'autre à regarder.
    if ((!text && photos.length === 0) || busy) return;
    const sending = photos.map((photo) => photo.id);
    setDraft("");
    setQueued(null);
    setPhotoError(null);
    // Un identifiant par envoi : c'est lui qui rend sûr le rejeu d'un message
    // resté en file hors ligne, et qui empêche un double clic de lancer deux
    // orchestrations.
    const clientMessageId = crypto.randomUUID();
    try {
      await live.send(text, clientMessageId, sending);
      photos.forEach((photo) => URL.revokeObjectURL(photo.preview));
      setPhotos([]);
    } catch (caught) {
      // Hors ligne *et* sans photo : la file sait attendre le réseau. Avec une
      // photo, non — la pièce jointe est déjà montée ou elle ne l'est pas.
      if (!navigator.onLine && sending.length === 0) {
        // Hors ligne : on repasse par la file, qui sait attendre le réseau.
        try {
          await send.mutateAsync({ content: text, clientMessageId });
        } catch (queuedError) {
          if ((queuedError as { name?: string })?.name === "QueuedOfflineError") {
            setQueued("Envoyé au retour du réseau.");
            return;
          }
          setDraft(text);
          throw queuedError;
        }
        return;
      }
      setDraft(text);
      throw caught;
    }
  }

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <SnapshotCard snapshot={detail.data.snapshot} />

      <Card className="flex min-h-0 flex-col">
        <CardHeader
          title={detail.data.conversation.title ?? "Nouveau fil"}
          subtitle={`${detail.data.conversation.last_sequence} message(s)`}
        />
        <CardBody className="flex flex-col gap-3">
          {history.hasNextPage && (
            <div className="flex justify-center">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => void history.fetchNextPage()}
                loading={history.isFetchingNextPage}
              >
                Charger les messages plus anciens
              </Button>
            </div>
          )}

          {messages.length === 0 && !history.isLoading && (
            <EmptyState
              title="Fil vide"
              description="Écrivez ce que vous cherchez. Les précisions données au fil des tours se cumulent."
            />
          )}

          <ul className="flex flex-col gap-3">
            {messages.map((message) => (
              <MessageRow key={message.id} message={message} conversationId={conversationId} />
            ))}
          </ul>
          {live.pendingQuestion && (
            <li className="flex flex-col gap-1 rounded-md border border-border bg-surface-sunken px-3 py-2 opacity-70">
              <span className="text-[11px] text-ink-subtle">Vous · à l'instant</span>
              <p className="whitespace-pre-wrap text-[14px] text-ink">{live.pendingQuestion}</p>
            </li>
          )}
          {busy && (
            <p className="flex items-center gap-2 text-[12px] text-ink-subtle" aria-live="polite">
              <Sparkles className="size-3.5 animate-pulse" aria-hidden />
              {live.step}
            </p>
          )}
          {live.error && (
            <p className="flex items-center gap-2 text-[12px] text-status-out">
              <AlertTriangle className="size-3.5" aria-hidden />
              {live.error}
            </p>
          )}
          <div ref={bottom} />


          {queued && <p className="text-[12px] text-ink-subtle">{queued}</p>}

          {photos.length > 0 && (
            <ul className="flex flex-wrap gap-2 pt-1">
              {photos.map((photo) => (
                <li key={photo.id} className="relative">
                  <img
                    src={photo.preview}
                    alt={photo.name}
                    className="size-16 rounded-md border border-border object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => drop(photo.id)}
                    aria-label={`Retirer ${photo.name}`}
                    className="absolute -right-1.5 -top-1.5 rounded-full border border-border bg-surface p-0.5 text-ink-subtle"
                  >
                    <X className="size-3" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {photoError && (
            <p className="flex items-center gap-2 text-[12px] text-status-out">
              <AlertTriangle className="size-3.5" aria-hidden />
              {photoError}
            </p>
          )}

          <VoiceStatus voice={voice} />

          <form
            className="flex items-end gap-2 border-t border-border pt-3"
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            <input
              ref={picker}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              className="hidden"
              onChange={(event) => void pick(event.target.files)}
            />
            <Button
              type="button"
              variant="ghost"
              aria-label="Joindre une photo"
              disabled={busy || photos.length >= MAX_PHOTOS || attach.isPending}
              loading={attach.isPending}
              onClick={() => picker.current?.click()}
            >
              <ImagePlus className="size-4" aria-hidden />
            </Button>
            {voice.state !== "unsupported" && (
              <Button
                type="button"
                variant={voice.state === "recording" ? "danger" : "ghost"}
                aria-label={
                  voice.state === "recording"
                    ? "Arrêter l'enregistrement"
                    : "Dicter la question"
                }
                disabled={busy || voice.state === "uploading" || voice.state === "transcribing"}
                loading={voice.state === "uploading" || voice.state === "transcribing"}
                onClick={() => (voice.state === "recording" ? voice.stop() : void voice.start())}
              >
                {voice.state === "recording" ? (
                  <Square className="size-4" aria-hidden />
                ) : (
                  <Mic className="size-4" aria-hidden />
                )}
              </Button>
            )}
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={2}
              placeholder="Je cherche une mèche…"
              aria-label="Votre message"
              className="min-w-0 flex-1 resize-none rounded-md border border-border bg-surface px-3 py-2 text-[14px]"
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void submit();
                }
              }}
            />
            <Button
              type="submit"
              loading={busy}
              disabled={(!draft.trim() && photos.length === 0) || busy}
            >
              <Send className="size-4" aria-hidden />
              Envoyer
            </Button>
          </form>

          {/* Ce que l'assistant sait faire aujourd'hui, dit sans emphase. La
              voix et les recommandations viennent après. */}
          <p className="text-[11px] text-ink-subtle">
            HD-Vault répond à partir de ce qui est enregistré, et cite ce sur quoi il
            s'appuie. Quand la donnée n'existe pas, il le dit plutôt que de deviner. Ce
            qu'il lit sur une photo est une observation, pas une fiche produit : rien
            n'est enregistré au catalogue depuis une conversation.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

function MessageRow({
  message,
  conversationId,
}: {
  message: ConversationMessage;
  conversationId: string;
}) {
  const parameters = Object.entries(message.parameters);
  const fromAssistant = message.role === "ASSISTANT";
  const confidence = message.confidence ? CONFIDENCE_LABELS[message.confidence] : null;
  // La forme du texte de recommandation est un contrat : l'orchestrateur
  // l'écrit déterministiquement (V1.5-08). Le relire ici affiche une liste
  // plutôt qu'un paragraphe, sans dupliquer une règle de classement.
  const recommendations =
    fromAssistant && message.intent === "PRODUCT_RECOMMENDATION"
      ? parseRecommendation(message.content)
      : [];

  return (
    <li
      className={
        fromAssistant
          ? "flex flex-col gap-1 rounded-md border border-brand/30 bg-brand-subtle/30 px-3 py-2"
          : "flex flex-col gap-1 rounded-md border border-border bg-surface-sunken px-3 py-2"
      }
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[11px] text-ink-subtle">
          {fromAssistant ? "HD-Vault" : "Vous"} · {formatRelative(message.created_at)}
        </span>
        <span className="flex flex-wrap items-center gap-2">
          {message.missing_required.length > 0 && (
            <StatusBadge tone="warning">
              manque&nbsp;: {missingLabels(message.missing_required)}
            </StatusBadge>
          )}
          {confidence && <StatusBadge tone={confidence.tone}>{confidence.label}</StatusBadge>}
          {message.is_degraded && (
            <span
              className="flex items-center gap-1 text-[11px] text-warning"
              title="Une source a manqué ou une limite a été atteinte. Ce qui est affiché reste utilisable."
            >
              <AlertTriangle className="size-3" aria-hidden />
              partiel
            </span>
          )}
        </span>
      </div>
      {message.content &&
        (recommendations.length > 0 ? (
          <RecommendationList items={recommendations} />
        ) : (
          <p className="whitespace-pre-wrap text-[14px] text-ink">{message.content}</p>
        ))}

      {message.attachments.length > 0 && (
        <ul className="flex flex-wrap gap-2 pt-0.5">
          {message.attachments.map((attachment) => (
            <li key={attachment.id} className="text-[11px] text-ink-subtle">
              {/* `Photo` et non `<img src>` : le média est derrière
                  l'authentification, et une balise ne porte pas de jeton. */}
              <Photo
                mediaId={attachment.media_id}
                alt="Photo jointe à la question"
                className="size-16 border border-border object-cover"
              />
              {attachment.status === "FAILED" && <span>non lue</span>}
            </li>
          ))}
        </ul>
      )}

      {/* Le geste proposé, s'il y en a un. **Rien n'a eu lieu** tant que
          personne n'a cliqué (V1.6-12.2). */}
      {message.proposals.map((detail) => (
        <ProposalCard key={detail.proposal.id} detail={detail} conversationId={conversationId} />
      ))}

      {/* La preuve, compacte. Jamais une citation inventée : chaque ligne vient
          d'un outil qui a lu la base. */}
      {message.evidence.length > 0 && (
        <details className="pt-0.5">
          <summary className="cursor-pointer text-[11px] text-ink-subtle">
            Sur quoi je m'appuie ({message.evidence.length})
          </summary>
          <ul className="flex flex-col gap-0.5 pt-1">
            {message.evidence.slice(0, 8).map((item, index) =>
              item.document ? (
                <DocumentCitation key={`${item.claim}-${index}`} item={item} />
              ) : (
                <li key={`${item.claim}-${index}`} className="text-[11px] text-ink-subtle">
                  {evidenceLine(item)}
                </li>
              ),
            )}
          </ul>
        </details>
      )}

      {fromAssistant && message.vision_ms > 0 && (
        <p className="text-[11px] text-ink-subtle">
          {timingLine(message.duration_ms, message.vision_ms)}
        </p>
      )}

      {parameters.length > 0 && (
        <ul className="flex flex-wrap gap-2 pt-0.5">
          {parameters.map(([name, value]) => (
            <li key={name} className="text-[11px] text-ink-subtle">
              {parameterLabel(name)} : {parameterValue(name, value)}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/**
 * Une recommandation, en liste (V1.5-08 §36).
 *
 * Chaque candidat porte sa certitude et les raisons qui l'ont classé là. Rien
 * n'est calculé ici : les phrases viennent du moteur, qui seul sait pourquoi un
 * produit est premier.
 */
/**
 * Une citation documentaire dans le fil (V1.5-09 §31).
 *
 * Compacte par défaut — document, révision, page — et l'extrait derrière un
 * dépliement. Une page entière injectée dans le fil rendrait la conversation
 * illisible sur un téléphone, et c'est précisément là qu'on pose la question,
 * debout devant un bac.
 */
/**
 * Ce que l'écran dit pendant qu'on dicte (V1.5-10 §24, §25).
 *
 * Chaque état a sa phrase, parce qu'ils ne demandent pas le même geste :
 * enregistrer se coupe, transcrire s'attend, échouer se reprend. Un « chargement
 * en cours » unique laisserait quelqu'un devant un écran figé sans savoir s'il
 * doit parler, attendre ou recommencer.
 */
function VoiceStatus({ voice }: { voice: VoiceNote }) {
  if (voice.state === "idle" || voice.state === "unsupported") return null;

  if (voice.state === "recording") {
    return (
      <p className="flex flex-wrap items-center gap-2 text-[12px] text-ink">
        <span className="inline-block size-2 animate-pulse rounded-full bg-status-out" aria-hidden />
        <span>Enregistrement… {voice.elapsed}</span>
        <button type="button" onClick={voice.stop} className="underline">
          Arrêter
        </button>
        <button type="button" onClick={voice.cancel} className="text-ink-subtle underline">
          Annuler
        </button>
      </p>
    );
  }

  if (voice.state === "uploading" || voice.state === "transcribing") {
    return (
      <p className="flex items-center gap-2 text-[12px] text-ink-subtle">
        {voice.state === "uploading" ? "Envoi de l'enregistrement…" : "Transcription en cours…"}
        <button type="button" onClick={voice.cancel} className="underline">
          Annuler
        </button>
      </p>
    );
  }

  if (voice.state === "ready") {
    return (
      <p className="flex flex-wrap items-center gap-2 text-[12px] text-ink-subtle">
        {/* Dit explicitement : ce texte vient d'un micro, pas d'un clavier. Et
            il est modifiable — un numéro de pièce mal entendu se répare en deux
            caractères, avant que la question ne parte chercher le mauvais
            produit. */}
        <span>Transcrit depuis un message vocal — relisez avant d'envoyer.</span>
        <button type="button" onClick={voice.reset} className="underline">
          Effacer
        </button>
      </p>
    );
  }

  return (
    <p className="flex flex-wrap items-center gap-2 text-[12px] text-status-out">
      <AlertTriangle className="size-3.5" aria-hidden />
      <span>{voice.error ?? "L'enregistrement n'a pas abouti."}</span>
      {voice.attachment && (
        <button type="button" onClick={() => void voice.retry()} className="underline">
          Réessayer
        </button>
      )}
      <button type="button" onClick={voice.reset} className="text-ink-subtle underline">
        Fermer
      </button>
    </p>
  );
}

function DocumentCitation({ item }: { item: AnswerEvidence }) {
  const reference = item.document;
  if (!reference) return null;
  const note = documentReadingNote(reference);
  return (
    <li className="text-[11px] text-ink-subtle">
      <details>
        <summary className="cursor-pointer">{documentCitation(reference)}</summary>
        <p className="border-l-2 border-line pl-2 pt-1 italic">
          « {shortExcerpt(item.value, 400)} »
        </p>
        {note && <p className="pt-0.5 text-warning">{note}</p>}
      </details>
    </li>
  );
}

function RecommendationList({ items }: { items: RecommendedItem[] }) {
  return (
    <ol className="flex flex-col gap-2">
      {items.map((item) => (
        <li
          key={item.rank}
          className="flex flex-col gap-1 rounded-md border border-border bg-surface px-3 py-2"
        >
          <span className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-[14px] text-ink">
              {item.rank}. {item.title}
            </span>
            {item.certainty && (
              <StatusBadge tone={recommendationTone(item.certainty)}>
                {item.certainty}
              </StatusBadge>
            )}
          </span>
          {item.reasons.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {item.reasons.map((reason) => (
                <li key={reason} className="text-[12px] text-ink-subtle">
                  · {reason}
                </li>
              ))}
            </ul>
          )}
          {item.warnings.length > 0 && (
            <ul className="flex flex-col gap-0.5">
              {item.warnings.map((warning) => (
                <li
                  key={warning}
                  className="flex items-center gap-1 text-[12px] text-warning"
                >
                  <AlertTriangle className="size-3 shrink-0" aria-hidden />
                  {warning}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ol>
  );
}

function SnapshotCard({ snapshot }: { snapshot: ConversationSnapshot }) {
  const parameters = Object.entries(snapshot.task.parameters);
  if (parameters.length === 0 && snapshot.entity_refs.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Ce que HD-Vault a retenu"
        subtitle="De quoi on parle, et ce que vous avez précisé. Aucun stock, aucun emplacement : ces choses-là se relisent."
      />
      <CardBody className="flex flex-col gap-2">
        {parameters.length > 0 && (
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[13px]">
            {parameters.map(([name, value]) => (
              <div key={name} className="flex justify-between gap-2">
                <dt className="text-ink-subtle">{parameterLabel(name)}</dt>
                <dd className="text-ink">{parameterValue(name, value)}</dd>
              </div>
            ))}
          </dl>
        )}
        {snapshot.task.missing_required.length > 0 && (
          <p className="text-[12px] text-warning">
            Il manque encore : {missingLabels(snapshot.task.missing_required)}.
          </p>
        )}
        {snapshot.entity_refs.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {snapshot.entity_refs.map((ref) => (
              <li key={`${ref.entity_type}-${ref.entity_id}`}>
                <StatusBadge tone="info">{ref.label || ref.entity_type}</StatusBadge>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}
